import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Eye, Filter, Download, Check } from "lucide-react";
import { Transaction } from "../../types/transaction";

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  previewData: Transaction[];
  selectedYear: string;
  handleImport: () => void;
  "aria-labelledby"?: string;
}

const PreviewDialog: React.FC<PreviewDialogProps> = (props) => {
  const { open, onClose, previewData, selectedYear, handleImport } = props;
  const [activeTab, setActiveTab] = useState(0);

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby={props["aria-labelledby"] || "preview-dialog-title"}
    >
      <DialogTitle id="preview-dialog-title">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Preview Transactions</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Chip
              icon={<Check size={16} />}
              label={`${previewData.length} transactions found`}
              color="success"
              variant="outlined"
              size="small"
            />
            <Tooltip title="Export preview to Excel">
              <IconButton size="small">
                <Download size={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filter transactions">
              <IconButton size="small">
                <Filter size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please review the transactions below before importing them into the
          system.
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            aria-label="transaction preview tabs"
          >
            <Tab label="All Transactions" />
            <Tab
              label="Validation Issues"
              icon={
                <Chip
                  label="0"
                  size="small"
                  color="success"
                  sx={{ height: 16, fontSize: "0.65rem" }}
                />
              }
              iconPosition="end"
            />
          </Tabs>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table
            stickyHeader
            size="small"
            aria-label="Transaction preview table"
          >
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Property Type</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Commission</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewData.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>{transaction.date}</TableCell>
                  <TableCell>{transaction.address}</TableCell>
                  <TableCell>{transaction.city}</TableCell>
                  <TableCell>{transaction.propertyType}</TableCell>
                  <TableCell align="right">
                    ${transaction.price.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    ${transaction.commission.toLocaleString()}
                  </TableCell>
                  <TableCell>{transaction.clientName}</TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.status}
                      size="small"
                      color="success"
                      sx={{
                        fontSize: "0.7rem",
                        textTransform: "capitalize",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            mt: 3,
            backgroundColor: "info.light",
            p: 2,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.main",
          }}
          role="alert"
        >
          <Typography variant="body2" color="info.dark">
            These {previewData.length} transactions will be imported for{" "}
            {selectedYear}. Please confirm that all data is correct before
            importing.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          color="primary"
          aria-label={`Import ${previewData.length} transactions`}
        >
          Import Now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewDialog;
